import ThemedElement from "shared/components/ThemedElement";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Box,
  Chip,
  Tooltip,
  Button,
} from "@mui/material";
import {
  Refresh,
  Storage,
  Lan,
  SettingsEthernet,
  CheckCircle,
  PriorityHigh,
  Visibility,
  Download,
  Edit,
} from "@mui/icons-material";
import PropTypes from "prop-types";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import DataTable from "shared/components/Tables/DataTable";
import { switchStats, switchDate } from "../../model/switchModel";
import { useSwitches } from "../../hooks/useSwitches";

// Componentes Separados
import SwitchStatusChip from "./components/SwitchStatusChip";
import SwitchToggle from "./components/SwitchToggle";
import ResetConfirmationDialog from "./components/ResetConfirmationDialog";
import ResetAllConfirmationDialog from "./components/ResetAllConfirmationDialog";
import ProcessingDialog from "./components/ProcessingDialog";
import StatisticsPanel from "./components/StatisticsPanel";
import TableHeader from "./components/TableHeader";
import SearchBar from "./components/SearchBar";
import PortsModal from "./components/PortsModal";
import SwitchEditDialog from "./components/SwitchEditDialog";
import SwitchCreateDialog from "./components/SwitchCreateDialog";

function TableRedes({ refreshFlag, onRefreshComplete, isTecnicoRedes = false }) {
  const {
    switchGroups,
    loading,
    resetLoading,
    resetAllLoading,
    statusChanging,
    exportLoading,
    error,
    selectedSwitch,
    selectedPort,
    notification,
    stats,
    lastUpdated,
    showNotification,
    closeNotification,
    fetchSwitches,
    exportSwitchesReport,
    resetPort,
    resetAllSwitches,
    changePortStatus,
    selectSwitchGroup,
    selectPortForReset,
    clearSelection,
    categorizeResponse,

    // Estados y funciones para creación/edición
    createDialogOpen,
    editDialogOpen,
    editingSwitch,
    softwareOptions,
    loadingSoftware,
    openCreateDialog,
    closeCreateDialog,
    openEditDialog,
    closeEditDialog,
    createSwitch,
    updateSwitch,
    prepareEditData,
  } = useSwitches();

  const [searchQuery, setSearchQuery] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetIndividualDialogOpen, setResetIndividualDialogOpen] = useState(false);
  const [processingDialogOpen, setProcessingDialogOpen] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [portsModalOpen, setPortsModalOpen] = useState(false);
  const [selectedPortForReset, setSelectedPortForReset] = useState(null);

  const initialFetchDone = React.useRef(false);

  // Efecto para carga inicial
  useEffect(() => {
    if (!initialFetchDone.current) {
      fetchSwitches();
      initialFetchDone.current = true;
    }
  }, [fetchSwitches]);

  // Efecto para refreshFlag
  useEffect(() => {
    if (refreshFlag && initialFetchDone.current) {
      fetchSwitches();
      if (onRefreshComplete) {
        onRefreshComplete();
      }
    }
  }, [refreshFlag, fetchSwitches, onRefreshComplete]);

  // Función para abrir el modal de puertos
  const handleViewPorts = useCallback(
    (switchGroup) => {
      selectSwitchGroup(switchGroup);
      setPortsModalOpen(true);
    },
    [selectSwitchGroup]
  );

  // Función para manejar edición - SOLO SI NO ES TECNICO REDES
  const handleEditSwitch = useCallback(
    (switchGroup) => {
      if (isTecnicoRedes) {
        showNotification("No tienes permisos para editar switches", "warning");
        return;
      }
      openEditDialog(switchGroup);
    },
    [openEditDialog, isTecnicoRedes, showNotification]
  );

  // Función para manejar creación - SOLO SI NO ES TECNICO REDES
  const handleCreateSwitch = useCallback(
    async (formData) => {
      if (isTecnicoRedes) {
        showNotification("No tienes permisos para crear switches", "warning");
        return { success: false, error: "Permiso denegado" };
      }
      const result = await createSwitch(formData);
      return result;
    },
    [createSwitch, isTecnicoRedes, showNotification]
  );

  // Función para manejar actualización - SOLO SI NO ES TECNICO REDES
  const handleUpdateSwitch = useCallback(
    async (formData) => {
      if (isTecnicoRedes) {
        showNotification("No tienes permisos para editar switches", "warning");
        return { success: false, error: "Permiso denegado" };
      }
      const result = await updateSwitch(formData);
      return result;
    },
    [updateSwitch, isTecnicoRedes, showNotification]
  );

  // Función para manejar clic en reiniciar desde el modal de puertos
  const handleResetClickFromModal = useCallback((port) => {
    setSelectedPortForReset(port);
    setResetIndividualDialogOpen(true);
  }, []);

  // Función para confirmar el reinicio individual
  const confirmIndividualReset = useCallback(async () => {
    if (!selectedPortForReset) return;

    setResetIndividualDialogOpen(false);
    try {
      setProcessingMessage(
        `Reiniciando puerto ${selectedPortForReset.port} del switch ${selectedPortForReset.nameswitch}...`
      );
      setProcessingDialogOpen(true);

      await resetPort(selectedPortForReset.portId);

      setTimeout(() => {
        setProcessingDialogOpen(false);
      }, 1000);
    } catch (error) {
      setProcessingDialogOpen(false);
      showNotification("Error al reiniciar el puerto", "error");
    } finally {
      setSelectedPortForReset(null);
    }
  }, [selectedPortForReset, resetPort, showNotification]);

  // Función para confirmar el reinicio masivo
  const confirmResetAll = useCallback(async () => {
    setResetDialogOpen(false);
    try {
      setProcessingMessage("Reiniciando todos los puertos habilitados...");
      setProcessingDialogOpen(true);

      await resetAllSwitches();

      setTimeout(() => {
        setProcessingDialogOpen(false);
      }, 1500);
    } catch (error) {
      setProcessingDialogOpen(false);
    }
  }, [resetAllSwitches]);

  // Función para exportar informe
  const handleExportReport = useCallback(async () => {
    try {
      await exportSwitchesReport();
    } catch (error) {
      console.error("Error al exportar informe:", error);
    }
  }, [exportSwitchesReport]);

  const formatDate = switchDate;

  // Función para formatear la versión (mostrar solo la primera parte)
  const formatVersion = useCallback((version) => {
    if (!version || version === "Versión no disponible") return "Versión no disponible";

    // Separar por comas y tomar la primera parte
    const parts = version.split(",");
    const firstPart = parts[0].trim();

    // Si es muy largo, truncarlo
    if (firstPart.length > 25) {
      return firstPart.substring(0, 22) + "...";
    }

    return firstPart;
  }, []);

  // Columnas de la tabla - SIEMPRE mostrar la columna de acciones
  const columns = useMemo(
    () => [
      {
        Header: "Nombre del Switch",
        accessor: "nombre",
        width: "20%",
      },
      { Header: "IP", accessor: "ip", width: "12%" },
      { Header: "Software", accessor: "version", width: "15%" },
      { Header: "Automático / Puertos", accessor: "puertos", width: "10%" },
      { Header: "Último intento · Bogotá", accessor: "ultimoReinicio", width: "15%" },
      { Header: "Acciones", accessor: "acciones", align: "center", width: "16%" },
    ],
    []
  );

  // Filtrar y ordenar switch groups
  const filteredSwitchGroups = useMemo(() => {
    let result = switchGroups;

    // Aplicar filtro de búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((group) => {
        if (!group) return false;
        return (
          (group.name || "").toLowerCase().includes(query) ||
          (group.ip || "").toLowerCase().includes(query) ||
          String(group.hardware || "")
            .toLowerCase()
            .includes(query) ||
          (group.version || "").toLowerCase().includes(query)
        );
      });
    }

    // Ordenar: grupos con puertos activos primero, luego por nombre
    return [...result].sort((a, b) => {
      if (a.activePorts > 0 && b.activePorts === 0) return -1;
      if (a.activePorts === 0 && b.activePorts > 0) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [switchGroups, searchQuery]);

  const filteredStats = useMemo(() => switchStats(filteredSwitchGroups), [filteredSwitchGroups]);

  // Crear las filas de la tabla
  const rows = useMemo(() => {
    return filteredSwitchGroups.map((group) => {
      const hasActivePorts = group.activePorts > 0;

      return {
        nombre: (
          <MDBox display="flex" alignItems="center">
            {hasActivePorts && (
              <Tooltip title="Tiene puertos habilitados para reinicio" arrow>
                <PriorityHigh
                  sx={{
                    color: "#2e7d32",
                    mr: 1,
                    fontSize: "1rem",
                  }}
                />
              </Tooltip>
            )}
            <MDBox>
              <MDTypography variant="button" fontWeight="medium" sx={{ color: "#424242" }}>
                <Lan sx={{ mr: 1, verticalAlign: "middle", fontSize: "1rem", color: "#1976d2" }} />
                {group.name?.replace(/_/g, " ") || "Sin nombre"}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#757575" }} display="block">
                {group.totalPorts} puertos configurados
              </MDTypography>
            </MDBox>
          </MDBox>
        ),
        ip: (
          <MDTypography
            variant="button"
            fontWeight="medium"
            sx={{
              color: "#0288d1",
              fontFamily: "'Roboto Mono', monospace",
              backgroundColor: "rgba(25, 118, 210, 0.08)",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: "0.75rem",
            }}
          >
            {group.ip || "Sin IP"}
          </MDTypography>
        ),
        version: (
          <Tooltip title={group.version || "Versión no disponible"} arrow>
            <Chip
              label={formatVersion(group.version)}
              size="small"
              sx={{
                color: "#0288d1",
                borderColor: "#0288d1",
                backgroundColor: "rgba(2, 136, 209, 0.08)",
              }}
              variant="outlined"
              icon={<Storage sx={{ color: "#0288d1" }} fontSize="small" />}
            />
          </Tooltip>
        ),
        puertos: (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={`${group.activePorts}/${group.totalPorts}`}
              size="small"
              sx={{
                backgroundColor: hasActivePorts ? "#2e7d32" : "transparent",
                color: hasActivePorts ? "#fff" : "#757575",
                borderColor: hasActivePorts ? "#2e7d32" : "#e0e0e0",
                fontWeight: hasActivePorts ? "bold" : "normal",
              }}
              variant={hasActivePorts ? "filled" : "outlined"}
            />
            <MDTypography variant="caption" sx={{ color: "#757575" }}>
              habilitados
            </MDTypography>
          </Box>
        ),
        ultimoReinicio: (
          <MDTypography variant="caption" sx={{ color: "#757575" }}>
            {formatDate(group.lastReset)}
          </MDTypography>
        ),
        acciones: (
          <Box display="flex" gap={1} justifyContent="center">
            <Tooltip title="Ver puertos" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    color: "#0288d1",
                    borderColor: "#0288d1",
                    "&:hover": {
                      borderColor: "#01579b",
                      backgroundColor: "rgba(2, 136, 209, 0.04)",
                    },
                    minWidth: 40,
                    px: 1,
                  }}
                  onClick={() => handleViewPorts(group)}
                >
                  <Visibility fontSize="small" />
                </Button>
              </span>
            </Tooltip>

            {/* Botón Editar - SOLO visible si NO es Técnico Redes */}
            {!isTecnicoRedes && (
              <Tooltip title="Editar switch" arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      color: "#1976d2",
                      borderColor: "#1976d2",
                      "&:hover": {
                        borderColor: "#0d47a1",
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                      },
                      minWidth: 40,
                      px: 1,
                    }}
                    onClick={() => handleEditSwitch(group)}
                    disabled={loadingSoftware}
                  >
                    {loadingSoftware ? <CircularProgress size={16} /> : <Edit fontSize="small" />}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        ),
      };
    });
  }, [
    filteredSwitchGroups,
    formatDate,
    formatVersion,
    handleViewPorts,
    handleEditSwitch,
    loadingSoftware,
    isTecnicoRedes,
  ]);

  return (
    <>
      <TableHeader
        title="Redes"
        stats={filteredStats}
        loading={loading}
        onRefresh={fetchSwitches}
        onCreateClick={openCreateDialog}
        creating={loadingSoftware}
        isTecnicoRedes={isTecnicoRedes}
      />

      <StatisticsPanel stats={filteredStats} lastUpdated={lastUpdated} />
      <MDBox px={2} pt={2} display="flex" justifyContent="space-between" alignItems="center">
        {/* Barra de búsqueda */}
        <Box sx={{ flexGrow: 1, mr: 2 }}>
          <MDBox
            component="form"
            sx={{
              display: "flex",
              alignItems: "center",
              maxWidth: 400,
            }}
          >
            <MDBox
              sx={{
                p: 1,
                backgroundColor: "background.paper",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                flexGrow: 1,
              }}
            >
              <ThemedElement
                component="input"
                type="text"
                placeholder="Buscar por nombre, IP, hardware o versión..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  width: "100%",
                  backgroundColor: "transparent",
                  fontSize: "0.875rem",
                  color: "#424242",
                }}
              />
            </MDBox>
          </MDBox>
        </Box>

        {/* Botones de acción */}
        <MDBox display="flex" gap={1}>
          {/* Botón de exportar informe */}
          <Tooltip title="Exportar informe completo de switches" arrow>
            <span>
              <Button
                variant="contained"
                color="success"
                startIcon={exportLoading ? <CircularProgress size={16} /> : <Download />}
                onClick={handleExportReport}
                disabled={exportLoading}
                sx={{
                  textTransform: "none",
                  fontWeight: "medium",
                  borderRadius: "8px",
                  px: 2,
                  backgroundColor: "#2e7d32",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#1b5e20",
                  },
                }}
              >
                {exportLoading ? "Exportando..." : "Exportar informe global"}
              </Button>
            </span>
          </Tooltip>

          {/* Botón de reinicio masivo - DISPONIBLE PARA Técnico Redes también */}
          <Tooltip
            title="Solicitar reinicio global de todos los puertos con automático habilitado"
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="warning"
                startIcon={resetAllLoading ? <CircularProgress size={16} /> : <Refresh />}
                onClick={() => setResetDialogOpen(true)}
                disabled={resetAllLoading || stats.activePorts === 0}
                sx={{
                  textTransform: "none",
                  fontWeight: "medium",
                  borderRadius: "8px",
                  px: 2,
                  backgroundColor: "#ff9800",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#e65100",
                  },
                  "&:disabled": {
                    backgroundColor: "rgba(255, 152, 0, 0.3)",
                    color: "rgba(255, 255, 255, 0.5)",
                  },
                }}
              >
                {resetAllLoading ? "Procesando..." : "Reinicio masivo global"}
              </Button>
            </span>
          </Tooltip>
        </MDBox>
      </MDBox>

      {/* Información de resultados */}
      <MDBox px={2} pt={1} display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="caption" sx={{ color: "#757575" }}>
          <PriorityHigh
            sx={{ fontSize: "0.75rem", mr: 0.5, verticalAlign: "middle", color: "#2e7d32" }}
          />
          Los switches con puertos habilitados para reinicio aparecen primero
        </MDTypography>

        <MDTypography variant="caption" sx={{ color: "#757575" }}>
          Mostrando {filteredSwitchGroups.length} de {switchGroups.length} switches
          {searchQuery && ` (filtrados por "${searchQuery}")`}
          {isTecnicoRedes && " • Modo solo lectura (sin crear/editar)"}
        </MDTypography>
      </MDBox>

      {error && switchGroups.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error} Se conservan los últimos datos disponibles.
        </Alert>
      )}
      {/* Tabla de switches */}
      <MDBox
        pt={2}
        sx={{ "& thead": { display: "table-header-group", p: 0 }, "& th": { bgcolor: "#eef3f8" } }}
      >
        {loading && !switchGroups.length ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress sx={{ color: "#0288d1" }} size={60} />
            <MDTypography variant="body2" sx={{ color: "#424242" }} ml={2}>
              Cargando switches...
            </MDTypography>
          </MDBox>
        ) : error && !switchGroups.length ? (
          <MDBox p={2}>
            <Alert
              severity="error"
              action={
                <IconButton sx={{ color: "#d32f2f" }} size="small" onClick={fetchSwitches}>
                  <Refresh />
                </IconButton>
              }
              sx={{
                "& .MuiAlert-icon": {
                  color: "#d32f2f",
                },
              }}
            >
              {error}
            </Alert>
          </MDBox>
        ) : filteredSwitchGroups.length === 0 ? (
          <MDBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
            p={3}
          >
            <Lan sx={{ fontSize: 60, color: "#616161", mb: 2 }} />
            <MDTypography variant="h6" sx={{ color: "#424242" }} gutterBottom>
              {searchQuery ? "No se encontraron resultados" : "No hay switches configurados"}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "#757575" }}>
              {searchQuery
                ? "Intenta con otros términos de búsqueda"
                : isTecnicoRedes
                ? "No hay switches disponibles para visualizar"
                : "Configura switches en el sistema"}
            </MDTypography>
          </MDBox>
        ) : (
          <DataTable
            table={{
              columns,
              rows,
            }}
            isSorted={true}
            entriesPerPage={{
              defaultValue: 25,
              entries: [10, 25, 50, 100, 200],
            }}
            showTotalEntries={true}
            pagination={{ variant: "gradient", color: "info" }}
            canSearch={false}
            autoResetPage={false}
            paginationResetKey={searchQuery}
            noEndBorder
          />
        )}
      </MDBox>

      {/* Modal para ver puertos - DISPONIBLE para todos */}
      <PortsModal
        open={portsModalOpen}
        onClose={() => {
          setPortsModalOpen(false);
          clearSelection();
        }}
        switchGroup={switchGroups.find((group) => group.id === selectedSwitch?.id) || null}
        busy={loading}
        selectedPort={selectedPortForReset}
        resetLoading={resetLoading}
        statusChanging={statusChanging}
        onPortSelect={selectPortForReset}
        onResetClick={handleResetClickFromModal}
        onChangePortStatus={changePortStatus}
        categorizeResponse={categorizeResponse}
        formatDate={formatDate}
      />

      {/* Modal de confirmación para reinicio individual - DISPONIBLE para todos */}
      <ResetConfirmationDialog
        open={resetIndividualDialogOpen}
        onClose={() => {
          setResetIndividualDialogOpen(false);
          setSelectedPortForReset(null);
        }}
        onConfirm={confirmIndividualReset}
        selectedSwitch={
          selectedPortForReset
            ? {
                name: selectedPortForReset.nameswitch,
                ip: selectedPortForReset.ipswitch,
                puerto: selectedPortForReset.port,
              }
            : null
        }
        resetLoading={resetLoading}
      />

      {/* Modal de confirmación para reinicio masivo - DISPONIBLE para todos */}
      <ResetAllConfirmationDialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onConfirm={confirmResetAll}
        switches={switchGroups}
        stats={stats}
        resetAllLoading={resetAllLoading}
      />

      {/* Diálogo de creación de switch - SOLO para administradores */}
      {!isTecnicoRedes && (
        <SwitchCreateDialog
          open={createDialogOpen}
          onClose={closeCreateDialog}
          onCreate={handleCreateSwitch}
          softwareOptions={softwareOptions}
          loading={loadingSoftware}
        />
      )}

      {/* Diálogo de edición de switch - SOLO para administradores */}
      {!isTecnicoRedes && (
        <SwitchEditDialog
          open={editDialogOpen}
          onClose={closeEditDialog}
          onUpdate={handleUpdateSwitch}
          switchData={editingSwitch ? prepareEditData(editingSwitch) : null}
          softwareOptions={softwareOptions}
          loading={loadingSoftware}
        />
      )}

      {/* Diálogo de procesamiento */}
      <ProcessingDialog open={processingDialogOpen} message={processingMessage} />

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          <MDTypography variant="body2" fontWeight="medium">
            {notification.message}
          </MDTypography>
        </Alert>
      </Snackbar>
    </>
  );
}

TableRedes.propTypes = {
  refreshFlag: PropTypes.bool,
  onRefreshComplete: PropTypes.func,
  isTecnicoRedes: PropTypes.bool,
};

TableRedes.defaultProps = {
  refreshFlag: false,
  onRefreshComplete: () => {},
  isTecnicoRedes: false,
};

export default TableRedes;
